import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSeo } from "../lib/useSeo";
import {
  adminLogin,
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  listInquiries,
  deleteInquiry,
  getSettings,
  updateSettings,
  sendTestAlert,
  listGallery,
  createGalleryItem,
  updateGalleryItem,
  deleteGalleryItem,
} from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { FileUploader } from "../components/FileUploader";
import { AdminReviews } from "../components/AdminReviews";
import { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Edit, Plus, LogOut, Save, X, Upload, Sun, MapPin, Calendar, Mail } from "lucide-react";

const EMPTY = { name: "", category: "", price: "", description: "", image_url: "", video_url: "" };
const EMPTY_GALLERY = { title: "", description: "", location: "", project_date: "", image_url: "" };
const DEFAULT_SETTINGS = {
  marquee_speed: 40,
  company_name: "Arnav Enterprises",
  tagline: "Renewable Energy Solution Provider",
  logo_url: "",
  hero_bg_url: "",
  whatsapp_number: "919503935678",
  notify_email: "",
  shop_address: "Gaddam Plot, Ashirwad Bhavan,\nNear Mulawalkar Hospital,\nAkola, Maharashtra",
  shop_phone: "+91 95039 35678",
  shop_email: "info@arnavsolar.in",
  shop_hours: "Mon–Sat: 10:00 AM – 8:00 PM",
  map_query: "Mulawalkar Hospital, Akola, Maharashtra",
};

export default function Admin() {
  useSeo({ title: "Admin", noindex: true });
  const location = useLocation();
  const navigate = useNavigate();

  const [token, setToken] = useState(localStorage.getItem("arnav_admin_token") || "");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("products");

  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  // Gallery state
  const [gallery, setGallery] = useState([]);
  const [galleryForm, setGalleryForm] = useState(EMPTY_GALLERY);
  const [galleryEditingId, setGalleryEditingId] = useState(null);

  const loadAll = async () => {
    try {
      const [p, s, g] = await Promise.all([listProducts(), getSettings(), listGallery()]);
      setProducts(p);
      setSettings({ ...DEFAULT_SETTINGS, ...s });
      setGallery(g);
      try {
        const i = await listInquiries();
        setInquiries(i);
      } catch {
        // ignore if token invalid
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (token) loadAll();
  }, [token]);

  // Auto-load a product for editing if URL has ?edit={id}
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get("edit");
    if (token && editId && products.length > 0) {
      const p = products.find((x) => x.id === editId);
      if (p) {
        startEdit(p);
        setTab("products");
        // clear the query param
        navigate("/admin", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await adminLogin(password);
      localStorage.setItem("arnav_admin_token", res.token);
      window.dispatchEvent(new Event("arnav-auth-change"));
      setToken(res.token);
      toast.success("Welcome back, admin.");
    } catch {
      toast.error("Wrong password");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("arnav_admin_token");
    window.dispatchEvent(new Event("arnav-auth-change"));
    setToken("");
    setPassword("");
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      category: p.category,
      price: String(p.price),
      description: p.description,
      image_url: p.image_url,
      video_url: p.video_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.price || !form.image_url) {
      toast.error("Name, category, price and image URL are required");
      return;
    }
    const payload = { ...form, price: parseFloat(form.price) };
    try {
      if (editingId) {
        await updateProduct(editingId, payload);
        toast.success("Product updated");
      } else {
        await createProduct(payload);
        toast.success("Product added");
      }
      setForm(EMPTY);
      setEditingId(null);
      loadAll();
    } catch {
      toast.error("Save failed. Are you logged in?");
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await deleteProduct(id);
      toast.success("Deleted");
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const removeInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await deleteInquiry(id);
      toast.success("Inquiry removed");
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const [testingEmail, setTestingEmail] = useState(false);
  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const r = await sendTestAlert();
      toast.success(`Test email sent to ${r.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  const saveAllSettings = async () => {
    try {
      await updateSettings({
        marquee_speed: Number(settings.marquee_speed) || 40,
        company_name: settings.company_name || "Arnav Enterprises",
        tagline: settings.tagline || "",
        logo_url: settings.logo_url || "",
        hero_bg_url: settings.hero_bg_url || "",
        whatsapp_number: (settings.whatsapp_number || "").replace(/\D/g, ""),
        notify_email: (settings.notify_email || "").trim(),
        shop_address: (settings.shop_address || "").trim(),
        shop_phone: (settings.shop_phone || "").trim(),
        shop_email: (settings.shop_email || "").trim(),
        shop_hours: (settings.shop_hours || "").trim(),
        map_query: (settings.map_query || "").trim(),
      });
      window.dispatchEvent(new Event("arnav-settings-change"));
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo must be under 500 KB. Please use a smaller image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSettings((s) => ({ ...s, logo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSetting = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });
  const setGal = (k) => (e) => setGalleryForm({ ...galleryForm, [k]: e.target.value });

  const startEditGallery = (g) => {
    setGalleryEditingId(g.id);
    setGalleryForm({
      title: g.title || "",
      description: g.description || "",
      location: g.location || "",
      project_date: g.project_date || "",
      image_url: g.image_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEditGallery = () => {
    setGalleryEditingId(null);
    setGalleryForm(EMPTY_GALLERY);
  };

  const submitGallery = async (e) => {
    e.preventDefault();
    if (!galleryForm.title || !galleryForm.image_url) {
      toast.error("Title and image are required");
      return;
    }
    try {
      if (galleryEditingId) {
        await updateGalleryItem(galleryEditingId, galleryForm);
        toast.success("Gallery item updated");
      } else {
        await createGalleryItem(galleryForm);
        toast.success("Added to gallery");
      }
      setGalleryForm(EMPTY_GALLERY);
      setGalleryEditingId(null);
      loadAll();
    } catch {
      toast.error("Save failed");
    }
  };

  const removeGallery = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    try {
      await deleteGalleryItem(id);
      toast.success("Deleted");
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="bg-white border border-border rounded-2xl p-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-2">Admin</div>
          <h1 className="font-heading text-3xl font-bold mb-6">Sign in</h1>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <div>
              <Label className="text-sm font-semibold mb-2 block">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                data-testid="admin-password-input"
                className="bg-white border-border rounded-lg h-11"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full bg-primary text-white hover:bg-orange-600 transition-colors font-medium rounded-full px-8 py-3"
            >
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  const tabs = ["products", "gallery", "reviews", "branding", "inquiries", "settings"];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-1">Admin</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout"
          className="inline-flex items-center gap-2 text-sm text-secondary border border-border bg-white rounded-full px-5 py-2 hover:border-secondary"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      <div className="flex gap-2 mb-6 sm:mb-8 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="admin-tabs">
        {tabs.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`shrink-0 px-5 py-2 rounded-full text-sm font-medium border transition-colors ${
              tab === t ? "bg-primary text-white border-primary shadow-sm" : "bg-white text-secondary border-border hover:border-primary hover:text-primary"
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === "inquiries" && inquiries.length > 0 && (
              <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${tab === t ? "bg-white text-primary" : "bg-primary text-white"}`}>{inquiries.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          <form
            onSubmit={submitProduct}
            data-testid="product-form"
            className="lg:col-span-2 min-w-0 bg-white border border-border rounded-2xl p-4 sm:p-6 space-y-4 h-fit lg:sticky lg:top-24"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-bold">
                {editingId ? "Edit product" : "Add new product"}
              </h3>
              {editingId && (
                <button type="button" onClick={cancelEdit} data-testid="cancel-edit-btn" className="text-muted-foreground hover:text-secondary">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Name *</Label>
              <Input value={form.name} onChange={set("name")} data-testid="pf-name" className="bg-white border-border rounded-lg h-11" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold mb-2 block">Category *</Label>
                <Input value={form.category} onChange={set("category")} data-testid="pf-category" placeholder="Solar Panel" className="bg-white border-border rounded-lg h-11" />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-2 block">Price (₹) *</Label>
                <Input type="number" value={form.price} onChange={set("price")} data-testid="pf-price" className="bg-white border-border rounded-lg h-11" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Description</Label>
              <Textarea value={form.description} onChange={set("description")} data-testid="pf-description" rows={3} className="bg-white border-border rounded-lg" />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Product image *</Label>
              <FileUploader
                kind="image"
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                testid="pf-image-upload"
              />
              <div className="mt-2">
                <Label className="text-xs font-semibold mb-1 block text-muted-foreground">Or paste image URL</Label>
                <Input
                  value={form.image_url?.startsWith("/api/") ? "" : form.image_url}
                  onChange={set("image_url")}
                  data-testid="pf-image"
                  placeholder="https://..."
                  className="bg-white border-border rounded-lg h-10 text-sm"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Product video (optional)</Label>
              <FileUploader
                kind="video"
                value={form.video_url}
                onChange={(url) => setForm({ ...form, video_url: url })}
                testid="pf-video-upload"
              />
              <div className="mt-2">
                <Label className="text-xs font-semibold mb-1 block text-muted-foreground">Or paste YouTube / MP4 URL</Label>
                <Input
                  value={form.video_url?.startsWith("/api/") ? "" : form.video_url}
                  onChange={set("video_url")}
                  data-testid="pf-video"
                  placeholder="https://youtu.be/..."
                  className="bg-white border-border rounded-lg h-10 text-sm"
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="pf-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-orange-600 transition-colors font-medium rounded-full px-8 py-3"
            >
              {editingId ? <><Save className="w-4 h-4" /> Save changes</> : <><Plus className="w-4 h-4" /> Add product</>}
            </button>
          </form>

          <div className="lg:col-span-3 min-w-0 space-y-4">
            {products.length === 0 && (
              <div className="bg-white border border-border rounded-2xl p-12 text-center text-muted-foreground">
                No products yet. Add your first product.
              </div>
            )}
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white border border-border rounded-2xl p-4 flex gap-4">
                <img src={p.image_url} alt={p.name} className="w-24 h-24 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-[0.15em] text-primary font-bold">{p.category}</div>
                  <div className="font-heading text-lg font-semibold leading-tight truncate">{p.name}</div>
                  <div className="text-sm text-muted-foreground line-clamp-1">{p.description}</div>
                  <div className="text-sm font-bold mt-1">₹{Number(p.price).toLocaleString("en-IN")}</div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => startEdit(p)}
                    data-testid={`edit-${p.id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border hover:border-secondary text-secondary"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeProduct(p.id)}
                    data-testid={`delete-${p.id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border hover:border-destructive text-destructive"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "gallery" && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          <form
            onSubmit={submitGallery}
            data-testid="gallery-form"
            className="lg:col-span-2 min-w-0 bg-white border border-border rounded-2xl p-4 sm:p-6 space-y-4 h-fit lg:sticky lg:top-24"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-bold">
                {galleryEditingId ? "Edit gallery item" : "Add gallery item"}
              </h3>
              {galleryEditingId && (
                <button type="button" onClick={cancelEditGallery} data-testid="cancel-gallery-edit" className="text-muted-foreground hover:text-secondary">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Title *</Label>
              <Input
                value={galleryForm.title}
                onChange={setGal("title")}
                data-testid="gf-title"
                placeholder="e.g. 5kW Rooftop Installation"
                className="bg-white border-border rounded-lg h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Description</Label>
              <Textarea
                value={galleryForm.description}
                onChange={setGal("description")}
                data-testid="gf-description"
                rows={3}
                placeholder="Short story about the project..."
                className="bg-white border-border rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold mb-2 block">Location</Label>
                <Input
                  value={galleryForm.location}
                  onChange={setGal("location")}
                  data-testid="gf-location"
                  placeholder="Akola, MH"
                  className="bg-white border-border rounded-lg h-11"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-2 block">Project date</Label>
                <Input
                  value={galleryForm.project_date}
                  onChange={setGal("project_date")}
                  data-testid="gf-date"
                  placeholder="Jan 2025"
                  className="bg-white border-border rounded-lg h-11"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Image *</Label>
              <FileUploader
                kind="image"
                value={galleryForm.image_url}
                onChange={(url) => setGalleryForm({ ...galleryForm, image_url: url })}
                testid="gf-image-upload"
              />
              <div className="mt-2">
                <Label className="text-xs font-semibold mb-1 block text-muted-foreground">Or paste image URL</Label>
                <Input
                  value={galleryForm.image_url?.startsWith("/api/") ? "" : galleryForm.image_url}
                  onChange={setGal("image_url")}
                  data-testid="gf-image-url"
                  placeholder="https://..."
                  className="bg-white border-border rounded-lg h-10 text-sm"
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="gf-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-orange-600 transition-colors font-medium rounded-full px-8 py-3"
            >
              {galleryEditingId ? <><Save className="w-4 h-4" /> Save changes</> : <><Plus className="w-4 h-4" /> Add to gallery</>}
            </button>
          </form>

          <div className="lg:col-span-3 min-w-0 space-y-4">
            {gallery.length === 0 && (
              <div className="bg-white border border-border rounded-2xl p-12 text-center text-muted-foreground">
                No projects yet. Add your first project to the gallery.
              </div>
            )}
            {gallery.map((g) => (
              <div key={g.id} data-testid={`admin-gallery-${g.id}`} className="bg-white border border-border rounded-2xl p-4 flex gap-4">
                <img src={fileUrl(g.image_url)} alt={g.title} className="w-28 h-28 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg font-semibold leading-tight truncate">{g.title}</div>
                  {g.description && (
                    <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{g.description}</div>
                  )}
                  <div className="flex flex-wrap gap-4 text-xs text-secondary mt-2">
                    {g.location && (
                      <div className="inline-flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                        <span>{g.location}</span>
                      </div>
                    )}
                    {g.project_date && (
                      <div className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                        <span>{g.project_date}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => startEditGallery(g)}
                    data-testid={`edit-gallery-${g.id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border hover:border-secondary text-secondary"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeGallery(g.id)}
                    data-testid={`delete-gallery-${g.id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border hover:border-destructive text-destructive"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {tab === "reviews" && <AdminReviews />}

      {tab === "branding" && (
        <div className="bg-white border border-border rounded-2xl p-8 max-w-2xl">
          <h3 className="font-heading text-2xl font-bold mb-1">Company branding</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Change your firm name, tagline and logo. Updates apply site-wide.
          </p>

          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold mb-2 block">Company name</Label>
              <Input
                value={settings.company_name}
                onChange={setSetting("company_name")}
                data-testid="brand-name-input"
                placeholder="Arnav Enterprises"
                className="bg-white border-border rounded-lg h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Tagline</Label>
              <Input
                value={settings.tagline}
                onChange={setSetting("tagline")}
                data-testid="brand-tagline-input"
                placeholder="Powering Akola with the Sun"
                className="bg-white border-border rounded-lg h-11"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">Logo</Label>
              <FileUploader
                kind="image"
                value={settings.logo_url}
                onChange={(url) => setSettings({ ...settings, logo_url: url })}
                testid="brand-logo-upload"
              />
              <div className="mt-3">
                <Label className="text-xs font-semibold mb-2 block text-muted-foreground">Or paste image URL</Label>
                <Input
                  value={settings.logo_url?.startsWith("data:") || settings.logo_url?.startsWith("/api/") ? "" : settings.logo_url}
                  onChange={setSetting("logo_url")}
                  data-testid="brand-logo-url-input"
                  placeholder="https://..."
                  className="bg-white border-border rounded-lg h-11"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">Home page hero background</Label>
              <FileUploader
                kind="image"
                value={settings.hero_bg_url}
                onChange={(url) => setSettings({ ...settings, hero_bg_url: url })}
                testid="hero-bg-upload"
              />
              <div className="mt-3">
                <Label className="text-xs font-semibold mb-2 block text-muted-foreground">Or paste image URL</Label>
                <Input
                  value={settings.hero_bg_url?.startsWith("/api/") ? "" : settings.hero_bg_url}
                  onChange={setSetting("hero_bg_url")}
                  data-testid="hero-bg-url-input"
                  placeholder="https://..."
                  className="bg-white border-border rounded-lg h-11"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Leave empty to use the default solar rooftop photo. Recommended: 1920×1080 landscape.
              </p>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">WhatsApp number</Label>
              <Input
                value={settings.whatsapp_number}
                onChange={setSetting("whatsapp_number")}
                data-testid="brand-whatsapp-input"
                placeholder="919503935678"
                className="bg-white border-border rounded-lg h-11"
              />
              <p className="text-xs text-muted-foreground mt-2">
                With country code, digits only (e.g. 919503935678). Used for the "Chat on WhatsApp" buttons on products. Leave empty to hide them.
              </p>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">Inquiry alert email</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={settings.notify_email}
                  onChange={setSetting("notify_email")}
                  data-testid="brand-notify-email-input"
                  placeholder="you@example.com"
                  className="bg-white border-border rounded-lg h-11"
                />
                <button
                  type="button"
                  onClick={sendTestEmail}
                  disabled={testingEmail}
                  data-testid="send-test-email-btn"
                  className="shrink-0 inline-flex items-center gap-2 text-sm font-medium border border-border bg-white rounded-lg px-4 h-11 hover:border-primary hover:text-primary disabled:opacity-50"
                >
                  <Mail className="w-4 h-4" /> {testingEmail ? "Sending..." : "Send test"}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                You'll get an email here the moment a customer submits an inquiry. Save settings first, then "Send test" to check it works. Leave empty to disable alerts.
              </p>
            </div>

            <div className="pt-4 border-t border-border">
              <h4 className="font-heading text-lg font-bold mb-1">Shop details</h4>
              <p className="text-xs text-muted-foreground mb-4">Shown in the footer, Contact page, home page and customer emails.</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Shop address</Label>
                  <Textarea
                    value={settings.shop_address}
                    onChange={setSetting("shop_address")}
                    data-testid="brand-shop-address-input"
                    rows={3}
                    placeholder={"Line 1\nLine 2\nCity, State"}
                    className="bg-white border-border rounded-lg"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Each line appears on its own line on the website.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Phone</Label>
                    <Input
                      value={settings.shop_phone}
                      onChange={setSetting("shop_phone")}
                      data-testid="brand-shop-phone-input"
                      placeholder="+91 95039 35678"
                      className="bg-white border-border rounded-lg h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Public email</Label>
                    <Input
                      type="email"
                      value={settings.shop_email}
                      onChange={setSetting("shop_email")}
                      data-testid="brand-shop-email-input"
                      placeholder="info@arnavsolar.in"
                      className="bg-white border-border rounded-lg h-11"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Shop hours</Label>
                  <Input
                    value={settings.shop_hours}
                    onChange={setSetting("shop_hours")}
                    data-testid="brand-shop-hours-input"
                    placeholder="Mon–Sat: 10:00 AM – 8:00 PM"
                    className="bg-white border-border rounded-lg h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Google Maps location</Label>
                  <Input
                    value={settings.map_query}
                    onChange={setSetting("map_query")}
                    data-testid="brand-map-query-input"
                    placeholder="Shop name + area, or coordinates like 20.7002,77.0082"
                    className="bg-white border-border rounded-lg h-11"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Used for the map on the Contact page and the "Get directions" button. Tip: open Google Maps, right-click your shop, copy the coordinates and paste them here for an exact pin. Leave empty to hide the map.
                  </p>
                </div>
              </div>
            </div>


            <button
              onClick={saveAllSettings}
              data-testid="save-branding-btn"
              className="inline-flex items-center gap-2 bg-primary text-white hover:bg-orange-600 transition-colors font-medium rounded-full px-8 py-3"
            >
              <Save className="w-4 h-4" /> Save branding
            </button>
          </div>
        </div>
      )}

      {tab === "inquiries" && (
        <div className="space-y-4">
          {inquiries.length === 0 ? (
            <div className="bg-white border border-border rounded-2xl p-12 text-center text-muted-foreground">
              No inquiries yet.
            </div>
          ) : (
            inquiries.map((i) => (
              <div key={i.id} data-testid={`inquiry-${i.id}`} className="bg-white border border-border rounded-2xl p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-heading text-xl font-semibold">{i.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(i.created_at).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={() => removeInquiry(i.id)}
                    data-testid={`inquiry-delete-${i.id}`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border hover:border-destructive text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-4 grid md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">Phone</div>
                    <div>{i.phone}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">Email</div>
                    <div className="truncate" title={i.email}>{i.email || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">Requirement</div>
                    <div>{i.requirement_kw || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">Address</div>
                    <div>{i.address || "—"}</div>
                  </div>
                </div>
                {i.message && (
                  <div className="mt-4 text-sm">
                    <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">Message</div>
                    <div className="text-secondary">{i.message}</div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {tab === "settings" && (
        <div className="bg-white border border-border rounded-2xl p-8 max-w-xl">
          <h3 className="font-heading text-2xl font-bold mb-1">Marquee settings</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Set the default scroll duration (in seconds) for the product marquee on the homepage. Higher = slower.
          </p>
          <Label className="text-sm font-semibold mb-2 block">
            Speed (seconds per loop): {settings.marquee_speed}
          </Label>
          <input
            type="range"
            min="10"
            max="90"
            step="5"
            value={settings.marquee_speed}
            onChange={(e) => setSettings({ ...settings, marquee_speed: Number(e.target.value) })}
            data-testid="admin-speed-slider"
            className="w-full accent-primary"
          />
          <button
            onClick={saveAllSettings}
            data-testid="save-settings-btn"
            className="mt-6 inline-flex items-center gap-2 bg-primary text-white hover:bg-orange-600 transition-colors font-medium rounded-full px-8 py-3"
          >
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      )}
    </div>
  );
}
